import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { bookingService } from '../../services/bookingService';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import AppIcon from '../../components/AppIcon';
import { cn } from '../../utils/cn';
import Button from '../../components/ui/Button';
import Toast from '../../components/ui/Toast';
import Image from '../../components/AppImage';
import { formatNumber } from '../../utils/formatNumber';
import {
  getWorkloadLabelAr,
  getDeliveredPhotosLabelAr,
  getShootingMethodLabelAr,
  getPhotographyTypeLabelAr,
  getProductPhysicalSizeLabelAr,
  getShootingPlaceTypeLabelAr,
} from '../../utils/bookingLabels';
import Deliveries from './Deliveries';

const EDITING_LABELS = {
  'professional-editing': 'تعديل احترافي متقدم',
  'basic-editing':        'تعديل أساسي (إضاءة وألوان)',
  'no-editing':           'صور أصلية بدون تعديل',
};
const translateEditingItem = (item) => EDITING_LABELS[item] || item;

const formatDurationAr = (duration) => {
  const n = Number(duration);
  if (!duration && duration !== 0) return 'غير محدد';
  if (n === 1) return 'ساعة';
  if (n === 2) return 'ساعتان';
  if (n >= 3 && n <= 10) return `${n} ساعات`;
  return `${n} ساعة`;
};

// Countdown Timer Component
const CountdownTimer = ({ expiresAt }) => {
  const [timeRemaining, setTimeRemaining] = useState(null);

  useEffect(() => {
    const calculateTimeRemaining = () => {
      if (!expiresAt) return null;
      const now = new Date();
      const expires = new Date(expiresAt);
      const diff = expires - now;
      
      if (diff <= 0) {
        setTimeRemaining({ expired: true });
        return;
      }
      
      const minutes = Math.floor(diff / 1000 / 60);
      const seconds = Math.floor((diff / 1000) % 60);
      
      setTimeRemaining({ minutes, seconds, expired: false });
    };
    
    calculateTimeRemaining();
    const interval = setInterval(calculateTimeRemaining, 1000);
    
    return () => clearInterval(interval);
  }, [expiresAt]);

  if (!timeRemaining) return null;
  
  if (timeRemaining.expired) {
    return (
      <span className="text-xs text-red-600 font-semibold bg-red-50 px-2 py-1 rounded">
        انتهت المدة
      </span>
    );
  }
  
  return (
    <span className="text-xs text-yellow-700 font-semibold bg-yellow-50 px-2 py-1 rounded">
      متبقي: {timeRemaining.minutes}:{String(timeRemaining.seconds).padStart(2, '0')}
    </span>
  );
};

const Bookings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => (tabFromUrl === 'delivery' ? 'delivery' : 'pending'));
  const [toast, setToast] = useState(null);
  const isInitialLoad = useRef(true);
  const { user } = useAuth();

  // state انسحاب المصور من حجز مؤكد
  const [withdrawBooking, setWithdrawBooking] = useState(null);
  const [withdrawReason, setWithdrawReason] = useState('');
  const [withdrawSubmitting, setWithdrawSubmitting] = useState(false);

  // حالة التعليق
  const [suspensionStatus, setSuspensionStatus] = useState(null); // { suspended, suspendedUntil }

  // Sync URL with tab (e.g. /bookings?tab=delivery)
  useEffect(() => {
    if (tabFromUrl === 'delivery' && activeTab !== 'delivery') setActiveTab('delivery');
    if (tabFromUrl && tabFromUrl !== 'delivery' && ['pending', 'awaiting_payment', 'confirmed', 'awaiting_delivery', 'completed', 'cancelled'].includes(tabFromUrl)) setActiveTab(tabFromUrl);
  }, [tabFromUrl]);

  const setActiveTabAndUrl = (tabId) => {
    setActiveTab(tabId);
    if (tabId === 'delivery') setSearchParams({ tab: 'delivery' });
    else setSearchParams(tabId ? { tab: tabId } : {});
  };

  useEffect(() => {
    // Initial load with loading state
    loadBookings();
    // SAFE MODE: PWA - Clear badge when user opens bookings page
    try {
      if (typeof localStorage !== 'undefined') localStorage.setItem('mashhad_pwa_badge', '0');
      if (typeof navigator.setAppBadge === 'function') navigator.setAppBadge(0).catch(() => {});
    } catch (_) {}
    // Fallback polling — 60 ثانية (Realtime يتولى التحديث الفوري)
    const interval = setInterval(() => {
      loadBookings();
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Realtime: تحديث فوري عند أي تغيير في حجوزات هذا المصور
  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase
      .channel(`bookings-photographer-${user.id}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'bookings',
        filter: `photographer_id=eq.${user.id}`,
      }, () => {
        loadBookings();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user?.id]);

  // visibilitychange: إعادة الجلب عند العودة للتطبيق من الخلفية
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') loadBookings();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    bookingService.checkPhotographerSuspension(user.id).then(setSuspensionStatus).catch(() => {});
  }, [user?.id]);

  const loadBookings = async () => {
    try {
      // Only show loading spinner on initial load
      if (isInitialLoad.current) {
        setLoading(true);
      }
      
      const data = await bookingService.getMyBookings('photographer');
      setBookings(data || []);
      
    } catch (error) {
      console.error('Failed to load bookings:', error);
      // Only show error toast on initial load
      if (isInitialLoad.current) {
        setToast({ icon: 'AlertCircle', iconColor: '#ef4444', title: 'خطأ', message: 'فشل تحميل الحجوزات' });
      }
    } finally {
      // Always hide loading spinner after initial load completes
      if (isInitialLoad.current) {
        isInitialLoad.current = false;
        setLoading(false);
      }
    }
  };

  const handleStatusUpdate = async (bookingId, newStatus) => {
    try {
      await bookingService.updateBookingStatus(bookingId, newStatus);
      setToast({ icon: 'CheckCircle', iconColor: '#22c55e', title: 'تم', message: 'تم تحديث حالة الحجز بنجاح' });
      // Refresh immediately after status update (without loading spinner)
      loadBookings();
    } catch (error) {
      console.error('Failed to update booking status:', error);
      setToast({ icon: 'AlertCircle', iconColor: '#ef4444', title: 'خطأ', message: 'فشل تحديث حالة الحجز' });
    }
  };

  const handleSubmitWithdraw = async () => {
    if (!withdrawBooking) return;
    setWithdrawSubmitting(true);
    try {
      await bookingService.requestCancellation(withdrawBooking.id, 'photographer', withdrawReason);
      setToast({ icon: 'CheckCircle', iconColor: '#22c55e', title: 'تم الانسحاب', message: 'تم إلغاء الحجز. سيُردّ للعميل مبلغه كاملاً.' });
      setWithdrawBooking(null);
      loadBookings();
    } catch (err) {
      setToast({ icon: 'AlertCircle', iconColor: '#ef4444', title: 'خطأ', message: err?.message || 'فشل إرسال طلب الانسحاب' });
    } finally {
      setWithdrawSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending_photographer_confirmation: { label: 'في انتظار التأكيد', color: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
      awaiting_payment: { label: 'في انتظار الدفع', color: 'bg-blue-100 text-blue-700 border-blue-300' },
      paid_confirmed: { label: 'مؤكد ومدفوع', color: 'bg-green-100 text-green-700 border-green-300' },
      session_started: { label: '🎬 الجلسة جارية', color: 'bg-purple-100 text-purple-700 border-purple-300' },
      awaiting_delivery: { label: 'في انتظار رفع الملفات', color: 'bg-orange-100 text-orange-700 border-orange-300' },
      completed: { label: 'مكتمل · تم التسليم', color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
      declined: { label: 'مرفوض', color: 'bg-red-100 text-red-700 border-red-300' },
      expired: { label: 'منتهي', color: 'bg-gray-100 text-gray-700 border-gray-300' },
      cancellation_requested: { label: '⏳ إلغاء قيد المراجعة', color: 'bg-orange-100 text-orange-700 border-orange-300' },
      cancelled_by_customer: { label: 'ملغي من العميل', color: 'bg-red-100 text-red-700 border-red-300' },
      cancelled_by_photographer: { label: 'ملغي من المصور', color: 'bg-red-100 text-red-700 border-red-300' },
      cancelled_by_admin: { label: 'ملغي من المشرف', color: 'bg-red-100 text-red-700 border-red-300' },
      // Legacy statuses for backward compatibility
      pending: { label: 'معلق', color: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
      confirmed: { label: 'مؤكد', color: 'bg-blue-100 text-blue-700 border-blue-300' },
      cancelled: { label: 'ملغي', color: 'bg-red-100 text-red-700 border-red-300' },
    };

    const config = statusConfig[status] || statusConfig.pending_photographer_confirmation;

    return (
      <span className={cn('px-3 py-1 rounded-full text-xs font-medium border', config.color)}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'غير محدد';
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'غير محدد';
    
    // Handle time string format (HH:MM or HH:MM:SS)
    if (typeof timeString === 'string' && timeString.includes(':')) {
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours, 10);
      const min = minutes || '00';
      
      if (isNaN(hour)) return timeString;
      
      // Format to Arabic 12-hour format
      if (hour === 0) {
        return `12:${min} ص`;
      } else if (hour < 12) {
        return `${hour}:${min} ص`;
      } else if (hour === 12) {
        return `12:${min} م`;
      } else {
        return `${hour - 12}:${min} م`;
      }
    }
    
    // Handle Date object
    const date = new Date(timeString);
    if (!isNaN(date.getTime())) {
      return date.toLocaleTimeString('ar-SA', {
        hour: '2-digit',
        minute: '2-digit',
      });
    }
    
    return 'غير محدد';
  };

  // Gregorian date with weekday for pending cards
  const formatDateGregorian = (dateString) => {
    if (!dateString) return 'غير محدد';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'غير محدد';
    return date.toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const CANCELLED_STATUSES = ['cancelled_by_customer', 'cancelled_by_admin', 'cancelled_by_photographer', 'declined', 'expired', 'cancelled', 'cancellation_requested'];

  const deliveryEligibleCount = bookings.filter((b) => ['awaiting_delivery', 'completed'].includes(b.status)).length;
  const tabs = [
    { id: 'pending', label: 'في انتظار التأكيد', count: bookings.filter((b) => b.status === 'pending_photographer_confirmation').length },
    { id: 'awaiting_payment', label: 'في انتظار الدفع', count: bookings.filter((b) => b.status === 'awaiting_payment').length },
    { id: 'confirmed', label: 'مؤكد', count: bookings.filter((b) => ['paid_confirmed', 'session_started'].includes(b.status)).length },
    { id: 'awaiting_delivery', label: 'رفع الملفات', count: bookings.filter((b) => b.status === 'awaiting_delivery').length },
    { id: 'delivery', label: 'التسليم', count: deliveryEligibleCount },
    { id: 'completed', label: 'مكتمل ✓', count: bookings.filter((b) => b.status === 'completed').length },
    { id: 'cancelled', label: 'ملغي/منتهي', count: bookings.filter((b) => CANCELLED_STATUSES.includes(b.status)).length },
  ];

  const filteredBookings = bookings.filter((booking) => {
    if (activeTab === 'delivery') return false;
    if (activeTab === 'pending') return booking.status === 'pending_photographer_confirmation';
    if (activeTab === 'awaiting_payment') return booking.status === 'awaiting_payment';
    if (activeTab === 'confirmed') return ['paid_confirmed', 'session_started'].includes(booking.status);
    if (activeTab === 'awaiting_delivery') return booking.status === 'awaiting_delivery';
    if (activeTab === 'completed') return booking.status === 'completed';
    if (activeTab === 'cancelled') return CANCELLED_STATUSES.includes(booking.status);
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-slate-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-28">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
          إدارة الحجوزات
        </h1>
      </div>

      {/* ── Suspension Banner ── */}
      {suspensionStatus?.suspended && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-2xl p-4" dir="rtl">
          <AppIcon name="ShieldOff" size={22} className="text-red-500 shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-red-700 text-sm">حسابك موقوف مؤقتاً عن قبول حجوزات جديدة</p>
            <p className="text-xs text-red-600 mt-0.5">
              بسبب انسحابات متكررة من حجوزات مؤكدة. سيُرفع التعليق تلقائياً في{' '}
              {suspensionStatus.suspendedUntil
                ? new Intl.DateTimeFormat('ar-SA', { day: 'numeric', month: 'long', year: 'numeric' }).format(suspensionStatus.suspendedUntil)
                : 'تاريخ لاحق'}.
              يمكنك التواصل مع الإدارة إذا كنت تعتقد أن هذا خطأ.
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-2">
        <div className="flex items-center gap-2 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTabAndUrl(tab.id)}
              className={cn(
                'px-4 py-2 rounded-xl font-medium text-sm whitespace-nowrap transition-colors flex items-center gap-2',
                activeTab === tab.id
                  ? 'bg-button text-button-foreground'
                  : 'text-slate-700 hover:bg-slate-100'
              )}
            >
              <span>{tab.label}</span>
              {tab.count > 0 && (
                <span
                  className={cn(
                    'px-2 py-0.5 rounded-full text-xs',
                    activeTab === tab.id ? 'bg-white/20' : 'bg-slate-200'
                  )}
                >
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* عند اختيار تبويب التسليم: عرض صفحة تسليم الصور والفيديوهات */}
      {activeTab === 'delivery' ? (
        <Deliveries embedded />
      ) : (
      /* Bookings List */
      <div className="space-y-4">
        {filteredBookings.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 text-center">
            <AppIcon name="CalendarX" size={48} color="#94a3b8" className="mx-auto mb-4 opacity-50" />
            <p className="text-slate-600" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
              لا توجد حجوزات في هذا القسم
            </p>
          </div>
        ) : (
          filteredBookings.map((booking) => {
            // Parse location details (TEXT column stored as JSON string)
            let locationDetails = null;
            let locationText = booking.location || 'غير محدد';
            if (booking.locationDetails) {
              try {
                locationDetails = typeof booking.locationDetails === 'string' 
                  ? JSON.parse(booking.locationDetails) 
                  : booking.locationDetails;
                if (locationDetails.address_text) {
                  locationText = locationDetails.address_text;
                }
              } catch (e) {
                // Keep default locationText
              }
            }

            // precise_location (JSONB column) — fallback source of lat/lng
            // After toCamelCase: precise_location → preciseLocation with {lat, lng}
            const preciseLocation = booking.preciseLocation || booking.precise_location || null;

            // Merge: if locationDetails lacks lat/lng but preciseLocation has them, add them
            if (preciseLocation?.lat && preciseLocation?.lng) {
              if (!locationDetails) {
                locationDetails = { lat: preciseLocation.lat, lng: preciseLocation.lng };
              } else if (!locationDetails.lat || !locationDetails.lng) {
                locationDetails = { ...locationDetails, lat: preciseLocation.lat, lng: preciseLocation.lng };
              }
            }

            // Get pricing snapshot
            const pricingSnapshot = booking.pricingSnapshot || booking.pricing_snapshot || {};
            const totalPrice = parseFloat(booking.finalPrice || booking.final_price_sar || booking.price || 0);
            const equipment = Array.isArray(booking.equipment) 
              ? booking.equipment 
              : Array.isArray(pricingSnapshot.equipment)
                ? pricingSnapshot.equipment
                : [];

            // Customer info
            const customer = booking.customer || {};
            const customerName = customer.full_name || customer.fullName || 'عميل';
            const customerEmail = customer.email || '';
            const customerPhone = customer.phone || '';
            const customerAvatar = customer.avatar_url || customer.avatarUrl || 'https://via.placeholder.com/150';

            return (
              <BookingCard 
                key={booking.id}
                booking={booking}
                locationDetails={locationDetails}
                locationText={locationText}
                pricingSnapshot={pricingSnapshot}
                totalPrice={totalPrice}
                equipment={equipment}
                customerName={customerName}
                customerEmail={customerEmail}
                customerPhone={customerPhone}
                customerAvatar={customerAvatar}
                getStatusBadge={getStatusBadge}
                formatDate={formatDate}
                formatDateGregorian={formatDateGregorian}
                formatTime={formatTime}
                formatNumber={formatNumber}
                getWorkloadLabelAr={getWorkloadLabelAr}
                getDeliveredPhotosLabelAr={getDeliveredPhotosLabelAr}
                getShootingMethodLabelAr={getShootingMethodLabelAr}
                handleStatusUpdate={handleStatusUpdate}
                loadBookings={loadBookings}
                setToast={setToast}
                navigateToDelivery={() => setActiveTabAndUrl('delivery')}
                onWithdraw={(b) => { setWithdrawReason(''); setWithdrawBooking(b); }}
              />
            );
          })
        )}
      </div>
      )}

      {/* ── Withdraw Modal ── */}
      {withdrawBooking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" dir="rtl">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-900">الانسحاب من الحجز</h3>
              <button onClick={() => setWithdrawBooking(null)} className="text-slate-400 hover:text-slate-600">
                <AppIcon name="X" size={20} />
              </button>
            </div>

            {/* Warning + penalties */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
              <p className="text-sm font-semibold text-amber-700 mb-1">⚠️ تنبيه مهم</p>
              <p className="text-xs text-amber-600 mb-3">الانسحاب المتكرر يؤثر سلباً على تقييمك ومرتبتك في منصة مشهد وقد يُعلَّق حسابك.</p>
              <div className="border-t border-amber-200 pt-2 space-y-1.5">
                {[
                  { times: 1, label: 'انسحاب واحد', penalty: 'تحذير فقط', icon: '⚠️', color: 'text-amber-700' },
                  { times: 2, label: 'انسحابان', penalty: 'إخفاء من الاستكشاف 7 أيام', icon: '👁', color: 'text-orange-700' },
                  { times: 3, label: '3 انسحابات فأكثر', penalty: 'تعليق الحساب 14 يوم', icon: '⛔', color: 'text-red-700' },
                ].map(row => {
                  const current = suspensionStatus?.withdrawalCount ?? 0;
                  const isActive = current === row.times || (row.times === 3 && current >= 3);
                  return (
                    <div key={row.times} className={`flex items-center justify-between rounded-lg px-2.5 py-1.5 ${isActive ? 'bg-amber-100 border border-amber-300' : 'bg-white/60'}`}>
                      <span className={`text-xs font-medium ${isActive ? 'text-amber-900 font-bold' : 'text-amber-700'}`}>
                        {row.icon} {row.label}
                        {isActive && <span className="mr-1 text-[10px] bg-amber-600 text-white px-1.5 py-0.5 rounded-full">أنت هنا</span>}
                      </span>
                      <span className={`text-xs font-semibold ${isActive ? row.color + ' font-bold' : 'text-amber-600'}`}>{row.penalty}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <textarea
              value={withdrawReason}
              onChange={e => setWithdrawReason(e.target.value)}
              placeholder="سبب الانسحاب (مطلوب)..."
              rows={3}
              className="w-full border border-slate-200 focus:border-primary rounded-xl px-3 py-2 text-sm resize-none focus:outline-none mb-4"
            />

            <div className="flex gap-3">
              <button
                onClick={() => setWithdrawBooking(null)}
                className="flex-1 py-3 rounded-xl border border-slate-200 text-slate-600 font-semibold hover:bg-slate-50 transition text-sm"
              >
                تراجع
              </button>
              <button
                onClick={handleSubmitWithdraw}
                disabled={withdrawSubmitting || !withdrawReason.trim()}
                className="flex-1 py-3 rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-bold transition text-sm flex items-center justify-center gap-2"
              >
                {withdrawSubmitting
                  ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />جاري الإرسال...</>
                  : 'تأكيد طلب الانسحاب'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <Toast
          notification={toast}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

const PIN_EARLY_MS = 30 * 60 * 1000;
const PIN_GRACE_AFTER_MIDNIGHT_MS = 3 * 60 * 60 * 1000;

function getBookingSessionStart(booking) {
  const rawDate = booking.bookingDate || booking.booking_date || booking.scheduledAt;
  if (!rawDate) return null;
  const d = new Date(rawDate);
  if (isNaN(d.getTime())) return null;
  const rawTime = booking.bookingTime || booking.booking_time;
  if (rawTime) {
    const [h, m] = String(rawTime).split(':').map(Number);
    d.setHours(Number.isFinite(h) ? h : 0, Number.isFinite(m) ? m : 0, 0, 0);
  }
  return d;
}

function getPinWindow(booking) {
  const sessionStart = getBookingSessionStart(booking);
  if (!sessionStart) return { canStartPin: false, pinWindowEnded: false };
  const now = new Date();
  const pinWindowStart = new Date(sessionStart.getTime() - PIN_EARLY_MS);
  const endOfDay = new Date(sessionStart);
  endOfDay.setHours(23, 59, 59, 999);
  const pinWindowEnd = new Date(endOfDay.getTime() + PIN_GRACE_AFTER_MIDNIGHT_MS);
  return {
    canStartPin: now >= pinWindowStart && now <= pinWindowEnd,
    pinWindowEnded: now > pinWindowEnd,
  };
}

// SAFE MODE: Separate component for booking card with collapsible state (UI ONLY)
const BookingCard = ({
  booking,
  locationDetails,
  locationText,
  pricingSnapshot,
  totalPrice,
  equipment,
  customerName,
  customerEmail,
  customerPhone,
  customerAvatar,
  getStatusBadge,
  formatDate,
  formatDateGregorian,
  formatTime,
  formatNumber,
  getWorkloadLabelAr,
  getDeliveredPhotosLabelAr,
  getShootingMethodLabelAr,
  handleStatusUpdate,
  loadBookings,
  setToast,
  navigateToDelivery,
  onWithdraw,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [actionLoading, setActionLoading] = useState(null); // 'accept' | 'decline' | 'complete' | 'pin_init' | 'pin_verify' | null
  const [pinPhase, setPinPhase] = useState(null); // null | 'generated' | 'verified'
  const [generatedPin, setGeneratedPin] = useState('');
  const [enteredPin, setEnteredPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [showSessionConfirm, setShowSessionConfirm] = useState(false);

  const existingPin = booking.sessionPin || booking.session_pin || '';
  useEffect(() => {
    if (existingPin && pinPhase === null) {
      setPinPhase('generated');
      setGeneratedPin(existingPin);
    }
  }, [existingPin, pinPhase]);

  const isPending = booking.status === 'pending_photographer_confirmation';
  const holdExpired = booking.holdExpiresAt ? new Date(booking.holdExpiresAt) < new Date() : false;

  const HIDE_CONTACT_STATUSES = [
    'pending_photographer_confirmation',
    'awaiting_payment',
    'cancelled_by_customer',
    'cancelled_by_photographer',
    'cancelled_by_admin',
    'expired',
    'cancelled',
    'declined',
  ];
  const showContactInfo = !HIDE_CONTACT_STATUSES.includes(booking.status);

  const resolvedWorkloadBucket = booking.workloadBucket || pricingSnapshot.workloadBucket;
  const resolvedProductPhysicalSize =
    booking.productPhysicalSizeBucket ||
    pricingSnapshot.productPhysicalSizeBucket ||
    pricingSnapshot.product_physical_size_bucket;
  const photographyTypeForCard =
    booking.photographyType || booking.serviceType || pricingSnapshot.photographyType;
  const resolvedShootingPlaceType =
    booking.shootingPlaceType ||
    booking.shooting_place_type ||
    pricingSnapshot.shootingPlaceType ||
    pricingSnapshot.shooting_place_type;

  const handleAccept = async () => {
    if (holdExpired) {
      setToast({ icon: 'AlertCircle', iconColor: '#ef4444', title: 'انتهت المهلة', message: 'انتهت مدة الانتظار. لا يمكن قبول الحجز الآن.' });
      loadBookings();
      return;
    }
    setActionLoading('accept');
    try {
      // Check suspension before accepting
      const photographerId = booking.assignedPhotographerId || booking.assigned_photographer_id || booking.photographerId;
      if (photographerId) {
        const { suspended, suspendedUntil } = await bookingService.checkPhotographerSuspension(photographerId);
        if (suspended) {
          const dateStr = suspendedUntil
            ? new Intl.DateTimeFormat('ar-SA', { day: 'numeric', month: 'long' }).format(suspendedUntil)
            : '';
          setToast({
            icon: 'AlertCircle', iconColor: '#ef4444', title: 'حسابك موقوف مؤقتاً',
            message: `لا يمكنك قبول حجوزات جديدة بسبب انسحابات متكررة. التعليق حتى ${dateStr}.`,
          });
          setActionLoading(null);
          return;
        }
      }
      await bookingService.acceptBooking(booking.id);
      setToast({ icon: 'CheckCircle', iconColor: '#22c55e', title: 'تم القبول', message: 'تم قبول الحجز بنجاح ✓' });
      loadBookings();
    } catch (error) {
      console.error('Failed to accept booking:', error);
      const msg = error.message || 'فشل قبول الحجز';
      setToast({
        icon: 'AlertCircle', iconColor: '#ef4444', title: 'خطأ',
        message: msg.includes('expired') || msg.includes('انتهت')
          ? 'انتهت مدة الانتظار. لا يمكن قبول الحجز الآن.'
          : msg,
      });
      loadBookings();
    } finally {
      setActionLoading(null);
    }
  };

  const handleDecline = async () => {
    setActionLoading('decline');
    try {
      await bookingService.declineBooking(booking.id);
      setToast({ icon: 'CheckCircle', iconColor: '#64748b', title: 'تم', message: 'تم رفض الحجز' });
      loadBookings();
    } catch (error) {
      console.error('Failed to decline booking:', error);
      setToast({ icon: 'AlertCircle', iconColor: '#ef4444', title: 'خطأ', message: 'فشل رفض الحجز' });
    } finally {
      setActionLoading(null);
    }
  };

  // ── بطاقة "في انتظار التأكيد" ─────────────────────────────────────────────
  if (isPending) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border-2 border-yellow-300 overflow-hidden">
        {/* Header strip */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 bg-yellow-50 border-b border-yellow-200 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            {getStatusBadge(booking.status)}
            {booking.holdExpiresAt && <CountdownTimer expiresAt={booking.holdExpiresAt} />}
          </div>
          <span className="text-xs text-slate-500 font-mono">#{booking.id?.substring(0, 8).toUpperCase()}</span>
        </div>

        <div className="p-4 space-y-4">
          {/* ── معلومات الحجز الأساسية (دائمة الظهور) ── */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {/* التاريخ */}
            <div className="col-span-2 sm:col-span-3 bg-slate-50 rounded-xl px-3 py-2.5 flex items-center gap-2">
              <AppIcon name="Calendar" size={16} className="text-primary flex-shrink-0" />
              <div>
                <p className="text-[10px] text-slate-400 leading-none mb-0.5">التاريخ</p>
                <p className="text-sm font-bold text-slate-900 leading-tight">
                  {formatDateGregorian(booking.bookingDate || booking.booking_date || booking.scheduledAt)}
                </p>
              </div>
            </div>

            {/* الوقت */}
            <div className="bg-slate-50 rounded-xl px-3 py-2.5 flex items-center gap-2">
              <AppIcon name="Clock" size={16} className="text-primary flex-shrink-0" />
              <div>
                <p className="text-[10px] text-slate-400 leading-none mb-0.5">الوقت</p>
                <p className="text-sm font-bold text-slate-900">
                  {formatTime(booking.bookingTime || booking.booking_time)}
                </p>
              </div>
            </div>

            {/* المدة */}
            <div className="bg-slate-50 rounded-xl px-3 py-2.5 flex items-center gap-2">
              <AppIcon name="Timer" size={16} className="text-primary flex-shrink-0" />
              <div>
                <p className="text-[10px] text-slate-400 leading-none mb-0.5">المدة</p>
                <p className="text-sm font-bold text-slate-900">{formatDurationAr(booking.duration)}</p>
              </div>
            </div>

            {/* المبلغ */}
            <div className="bg-primary/5 border border-primary/20 rounded-xl px-3 py-2.5 flex items-center gap-2">
              <AppIcon name="Banknote" size={16} className="text-primary flex-shrink-0" />
              <div>
                <p className="text-[10px] text-slate-400 leading-none mb-0.5">المبلغ</p>
                <p className="text-sm font-bold text-primary">{formatNumber(totalPrice)} ر.س</p>
              </div>
            </div>

            {/* المدينة */}
            <div className="bg-slate-50 rounded-xl px-3 py-2.5 flex items-center gap-2">
              <AppIcon name="Building2" size={16} className="text-primary flex-shrink-0" />
              <div>
                <p className="text-[10px] text-slate-400 leading-none mb-0.5">المدينة</p>
                <p className="text-sm font-bold text-slate-900">{booking.city || 'غير محدد'}</p>
              </div>
            </div>

            {/* نوع التصوير */}
            <div className="bg-slate-50 rounded-xl px-3 py-2.5 flex items-center gap-2">
              <AppIcon name="Camera" size={16} className="text-primary flex-shrink-0" />
              <div>
                <p className="text-[10px] text-slate-400 leading-none mb-0.5">نوع التصوير</p>
                <p className="text-sm font-bold text-slate-900 line-clamp-1">
                  {getPhotographyTypeLabelAr(booking.photographyType || booking.photography_type || booking.serviceType)}
                </p>
              </div>
            </div>

            {/* الموقع (كامل العرض) */}
            <div className="col-span-2 sm:col-span-3 bg-slate-50 rounded-xl px-3 py-2.5">
              <div className="flex items-start gap-2">
                <AppIcon name="MapPin" size={16} className="text-primary flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] text-slate-400 leading-none mb-0.5">الموقع الجغرافي</p>
                  <p className="text-sm font-bold text-slate-900 break-words">{locationText || 'غير محدد'}</p>
                  {locationDetails?.district && (
                    <p className="text-xs text-slate-500 mt-0.5">حي: {locationDetails.district}</p>
                  )}
                </div>
                {locationDetails?.lat && locationDetails?.lng && (
                  <a
                    href={`https://maps.google.com/?q=${Number(locationDetails.lat).toFixed(7)},${Number(locationDetails.lng).toFixed(7)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-shrink-0 flex items-center gap-1 px-2 py-1 bg-primary text-white rounded-lg text-xs font-medium hover:bg-primary/90 transition-colors"
                  >
                    <AppIcon name="Map" size={12} color="white" />
                    <span>خريطة</span>
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* ── أزرار القبول والرفض ── */}
          {holdExpired ? (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-center">
              <p className="text-sm text-red-700 font-semibold">انتهت مدة الانتظار</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleAccept}
                disabled={actionLoading !== null}
                className="flex items-center justify-center gap-2 px-4 py-3.5 bg-green-600 hover:bg-green-700 active:bg-green-800 disabled:opacity-60 text-white rounded-xl font-bold text-base transition-colors select-none"
              >
                {actionLoading === 'accept' ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <AppIcon name="Check" size={20} color="white" />
                )}
                <span>متوفر</span>
              </button>
              <button
                onClick={handleDecline}
                disabled={actionLoading !== null}
                className="flex items-center justify-center gap-2 px-4 py-3.5 bg-red-600 hover:bg-red-700 active:bg-red-800 disabled:opacity-60 text-white rounded-xl font-bold text-base transition-colors select-none"
              >
                {actionLoading === 'decline' ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <AppIcon name="X" size={20} color="white" />
                )}
                <span>غير متاح</span>
              </button>
            </div>
          )}

          {/* ── توسيع: بيانات العميل والتسعير ── */}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors w-full justify-center py-1"
          >
            <span>{isExpanded ? 'إخفاء تفاصيل إضافية' : 'عرض تفاصيل إضافية (العميل، التسعير…)'}</span>
            <AppIcon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={15} />
          </button>

          {isExpanded && (
            <div className="pt-3 border-t border-slate-100 space-y-3">
              {/* معلومات العميل */}
              <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 bg-primary/10 flex items-center justify-center">
                    {customerAvatar && customerAvatar !== 'https://via.placeholder.com/150' ? (
                      <Image src={customerAvatar} alt={customerName} className="w-full h-full object-cover" />
                    ) : (
                      <AppIcon name="User" size={22} className="text-primary" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-slate-900 mb-1">{customerName}</p>
                    {showContactInfo && customerPhone && (
                      <a href={`tel:${customerPhone}`} className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-primary">
                        <AppIcon name="Phone" size={13} className="text-slate-400" />
                        {customerPhone}
                      </a>
                    )}
                    {showContactInfo && customerPhone && (
                      <a href={`https://wa.me/${customerPhone?.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm text-green-600 hover:text-green-700">
                        <AppIcon name="MessageCircle" size={13} className="text-green-500" />
                        واتساب
                      </a>
                    )}
                  </div>
                </div>
              </div>

              {/* متطلبات الجلسة */}
              {(() => {
                const methods = pricingSnapshot?.shootingMethods || (booking.shootingMethod ? [booking.shootingMethod] : []);
                const expLevel = (() => {
                  const lvl = pricingSnapshot?.experienceLevel;
                  if (lvl) {
                    const map = { certified: 'معتمد', beginner: 'معتمد', intermediate: 'مميز', distinguished: 'مميز', advanced: 'مميز', elite: 'نخبة', expert: 'نخبة' };
                    return map[lvl] || pricingSnapshot?.experienceLevelLabel || lvl;
                  }
                  return pricingSnapshot?.experienceLevelLabel || null;
                })();
                const hasSessionReqs = methods.length > 0 || resolvedWorkloadBucket || resolvedShootingPlaceType || (photographyTypeForCard === 'product' && resolvedProductPhysicalSize) || expLevel;
                if (!hasSessionReqs) return null;
                return (
                  <div className="bg-white border border-slate-200 rounded-xl p-3">
                    <p className="text-xs font-semibold text-slate-700 mb-2">متطلبات الجلسة</p>
                    <div className="grid grid-cols-2 gap-2">
                      {methods.length > 0 && (
                        <div className="bg-amber-50 rounded-lg px-3 py-2">
                          <p className="text-[10px] text-amber-500 mb-0.5">وسيلة التصوير</p>
                          <p className="text-xs font-bold text-amber-800">
                            {methods.map(m => getShootingMethodLabelAr(m)).filter(Boolean).join('، ')}
                          </p>
                        </div>
                      )}
                      {resolvedWorkloadBucket && (
                        <div className="bg-green-50 rounded-lg px-3 py-2">
                          <p className="text-[10px] text-green-500 mb-0.5">حجم العمل</p>
                          <p className="text-xs font-bold text-green-800">
                            {getWorkloadLabelAr(photographyTypeForCard, resolvedWorkloadBucket)}
                          </p>
                        </div>
                      )}
                      {resolvedShootingPlaceType && (
                        <div className="bg-sky-50 rounded-lg px-3 py-2">
                          <p className="text-[10px] text-sky-500 mb-0.5">مكان التصوير</p>
                          <p className="text-xs font-bold text-sky-800">
                            {getShootingPlaceTypeLabelAr(resolvedShootingPlaceType)}
                          </p>
                        </div>
                      )}
                      {expLevel && (
                        <div className="bg-violet-50 rounded-lg px-3 py-2">
                          <p className="text-[10px] text-violet-500 mb-0.5">مستوى الخبرة المطلوب</p>
                          <p className="text-xs font-bold text-violet-800">{expLevel}</p>
                        </div>
                      )}
                      {photographyTypeForCard === 'product' && resolvedProductPhysicalSize && (
                        <div className="bg-teal-50 rounded-lg px-3 py-2 col-span-2">
                          <p className="text-[10px] text-teal-600 mb-0.5">حجم المنتجات</p>
                          <p className="text-xs font-bold text-teal-900 leading-snug">
                            {getProductPhysicalSizeLabelAr(resolvedProductPhysicalSize)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* مستوى التعديل */}
              {equipment.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl p-3">
                  <p className="text-xs font-semibold text-slate-700 mb-2">مستوى التعديل</p>
                  <div className="flex flex-wrap gap-1.5">
                    {equipment.map((item, index) => (
                      <span key={index} className="px-2 py-1 bg-primary/10 text-primary rounded-lg text-xs font-medium">
                        {translateEditingItem(item)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* عدد المخرجات */}
              {(() => {
                const photosCount = pricingSnapshot?.photosCount ?? pricingSnapshot?.photos_count;
                const videosCount = pricingSnapshot?.videosCount ?? pricingSnapshot?.videos_count;
                const shortVideosCount = pricingSnapshot?.shortVideosCount ?? pricingSnapshot?.short_videos_count;
                const deliverableLabel = pricingSnapshot?.deliverableLabel ?? pricingSnapshot?.deliverable_label;
                const hasDeliverables = pricingSnapshot?.deliverableMode !== 'unspecified' && (photosCount > 0 || videosCount > 0 || shortVideosCount > 0 || deliverableLabel);
                if (!hasDeliverables) return null;
                const label = deliverableLabel || [
                  photosCount > 0 ? `${photosCount} صورة` : null,
                  videosCount > 0 ? `${videosCount} فيديو طويل` : null,
                  shortVideosCount > 0 ? `${shortVideosCount} فيديو قصير` : null,
                ].filter(Boolean).join(' + ');
                return (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                    <p className="text-xs font-semibold text-slate-700 mb-1">عدد المخرجات المطلوبة</p>
                    <p className="text-sm font-bold text-blue-800">{label}</p>
                  </div>
                );
              })()}

              {/* ملاحظات العميل */}
              {booking.notes && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                  <p className="text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1">
                    <AppIcon name="MessageSquare" size={12} className="text-amber-500" />
                    ملاحظات العميل
                  </p>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{booking.notes}</p>
                </div>
              )}

            </div>
          )}
        </div>
      </div>
    );
  }

  // ── بطاقات الحالات الأخرى (في انتظار الدفع، مؤكد، مكتمل) ─────────────────
  const sessionDate = new Date(booking.scheduledAt || booking.bookingDate || booking.booking_date);
  const today = new Date();
  const daysUntilSession = Math.ceil((sessionDate - today) / (1000 * 60 * 60 * 24));
  const sessionIsPast = sessionDate <= today;
  const sessionIsToday = sessionDate.toDateString() === today.toDateString();
  const { canStartPin, pinWindowEnded } = getPinWindow(booking);

  // Header strip color per status
  const statusTheme = {
    awaiting_payment:  { border: 'border-blue-300',  header: 'bg-blue-50 border-b border-blue-200' },
    paid_confirmed:    { border: 'border-green-300', header: 'bg-green-50 border-b border-green-200' },
    session_started:   { border: 'border-purple-400', header: 'bg-purple-50 border-b border-purple-200' },
    awaiting_delivery: { border: 'border-amber-300', header: 'bg-amber-50 border-b border-amber-200' },
    completed:         { border: 'border-slate-300', header: 'bg-slate-50 border-b border-slate-200' },
  };
  const theme = statusTheme[booking.status] || { border: 'border-slate-200', header: 'bg-slate-50 border-b border-slate-200' };

  return (
    <div className={`bg-white rounded-2xl shadow-sm border-2 ${theme.border} overflow-hidden`}>

      {/* Header strip */}
      <div className={`flex items-center justify-between gap-3 px-4 py-3 ${theme.header} flex-wrap`}>
        <div className="flex items-center gap-2 flex-wrap">
          {getStatusBadge(booking.status)}
        </div>
        <span className="text-xs text-slate-500 font-mono">#{booking.id?.substring(0, 8).toUpperCase()}</span>
      </div>

      <div className="p-4 space-y-4">

        {/* ── ملخص الحجز (دائم الظهور) ── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {/* التاريخ — كامل العرض */}
          <div className="col-span-2 sm:col-span-3 bg-slate-50 rounded-xl px-3 py-2.5 flex items-center gap-2">
            <AppIcon name="Calendar" size={16} className="text-primary flex-shrink-0" />
            <div>
              <p className="text-[10px] text-slate-400 leading-none mb-0.5">التاريخ</p>
              <p className="text-sm font-bold text-slate-900 leading-tight">
                {formatDateGregorian(booking.bookingDate || booking.booking_date || booking.scheduledAt)}
              </p>
            </div>
          </div>

          {/* الوقت */}
          <div className="bg-slate-50 rounded-xl px-3 py-2.5 flex items-center gap-2">
            <AppIcon name="Clock" size={16} className="text-primary flex-shrink-0" />
            <div>
              <p className="text-[10px] text-slate-400 leading-none mb-0.5">الوقت</p>
              <p className="text-sm font-bold text-slate-900">{formatTime(booking.bookingTime || booking.booking_time)}</p>
            </div>
          </div>

          {/* المدة */}
          <div className="bg-slate-50 rounded-xl px-3 py-2.5 flex items-center gap-2">
            <AppIcon name="Timer" size={16} className="text-primary flex-shrink-0" />
            <div>
              <p className="text-[10px] text-slate-400 leading-none mb-0.5">المدة</p>
              <p className="text-sm font-bold text-slate-900">{formatDurationAr(booking.duration)}</p>
            </div>
          </div>

          {/* المبلغ */}
          <div className="bg-primary/5 border border-primary/20 rounded-xl px-3 py-2.5 flex items-center gap-2">
            <AppIcon name="Banknote" size={16} className="text-primary flex-shrink-0" />
            <div>
              <p className="text-[10px] text-slate-400 leading-none mb-0.5">المبلغ</p>
              <p className="text-sm font-bold text-primary">{formatNumber(totalPrice)} ر.س</p>
            </div>
          </div>

          {/* المدينة */}
          <div className="bg-slate-50 rounded-xl px-3 py-2.5 flex items-center gap-2">
            <AppIcon name="Building2" size={16} className="text-primary flex-shrink-0" />
            <div>
              <p className="text-[10px] text-slate-400 leading-none mb-0.5">المدينة</p>
              <p className="text-sm font-bold text-slate-900">{booking.city || 'غير محدد'}</p>
            </div>
          </div>

          {/* نوع التصوير */}
          <div className="bg-slate-50 rounded-xl px-3 py-2.5 flex items-center gap-2">
            <AppIcon name="Camera" size={16} className="text-primary flex-shrink-0" />
            <div>
              <p className="text-[10px] text-slate-400 leading-none mb-0.5">نوع التصوير</p>
              <p className="text-sm font-bold text-slate-900 line-clamp-1">
                {getPhotographyTypeLabelAr(booking.photographyType || booking.photography_type || booking.serviceType)}
              </p>
            </div>
          </div>

          {/* الموقع — كامل العرض */}
          <div className="col-span-2 sm:col-span-3 bg-slate-50 rounded-xl px-3 py-2.5">
            <div className="flex items-start gap-2">
              <AppIcon name="MapPin" size={16} className="text-primary flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-slate-400 leading-none mb-0.5">الموقع الجغرافي</p>
                <p className="text-sm font-bold text-slate-900 break-words">{locationText || 'غير محدد'}</p>
                {locationDetails?.district && (
                  <p className="text-xs text-slate-500 mt-0.5">حي: {locationDetails.district}</p>
                )}
              </div>
              {locationDetails?.lat && locationDetails?.lng && (
                <a
                  href={`https://maps.google.com/?q=${Number(locationDetails.lat).toFixed(7)},${Number(locationDetails.lng).toFixed(7)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-shrink-0 flex items-center gap-1 px-2 py-1 bg-primary text-white rounded-lg text-xs font-medium hover:bg-primary/90 transition-colors"
                >
                  <AppIcon name="Map" size={12} color="white" />
                  <span>خريطة</span>
                </a>
              )}
            </div>
          </div>
        </div>

        {/* ── قسم خاص بكل حالة ── */}

        {/* في انتظار الدفع */}
        {booking.status === 'awaiting_payment' && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-start gap-2">
            <AppIcon name="CreditCard" size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-blue-800">بانتظار إتمام الدفع من العميل</p>
              {booking.paymentExpiresAt && (() => {
                const payExpiry = new Date(booking.paymentExpiresAt);
                const minsLeft = Math.max(0, Math.ceil((payExpiry - new Date()) / 60000));
                return minsLeft > 0
                  ? <p className="text-xs text-blue-600 mt-0.5">الوقت المتبقي للدفع: {minsLeft} دقيقة</p>
                  : <p className="text-xs text-red-600 mt-0.5">انتهت مهلة الدفع</p>;
              })()}
            </div>
          </div>
        )}

        {/* إلغاء قيد المراجعة */}
        {booking.status === 'cancellation_requested' && (
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 flex items-center gap-2">
            <AppIcon name="Clock" size={18} className="text-orange-500 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-orange-800">العميل طلب إلغاء الحجز</p>
              <p className="text-xs text-orange-700">الطلب قيد مراجعة الإدارة. الاسترداد حسب سياسة الإلغاء وقد يُخصم جزء من المبلغ.</p>
            </div>
          </div>
        )}

        {/* مؤكد ومدفوع — نظام PIN */}
        {booking.status === 'paid_confirmed' && (
          <div className="space-y-3">
            {/* شريط الحالة */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <AppIcon name="CheckCircle" size={18} className="text-green-600 flex-shrink-0" />
                <p className="text-sm font-semibold text-green-800">الجلسة مؤكدة ومدفوعة</p>
              </div>
              <div className="flex items-center gap-2">
                {!sessionIsPast && daysUntilSession >= 0 && (
                  <span className="text-xs font-bold text-green-700 bg-green-100 px-2 py-1 rounded-lg whitespace-nowrap">
                    {sessionIsToday ? '⚡ اليوم!' : `⏳ بعد ${daysUntilSession} يوم`}
                  </span>
                )}
                {pinPhase === null && (
                  <button
                    onClick={() => onWithdraw?.(booking)}
                    className="text-xs text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 px-2 py-1 rounded-lg font-semibold transition whitespace-nowrap"
                  >
                    طلب انسحاب
                  </button>
                )}
              </div>
            </div>

            {/* ── نظام PIN: من وقت الجلسة (قبلها 30 د) حتى نهاية اليوم + مهلة 3 ساعات ── */}
            {canStartPin && pinPhase === null && (
              <div className="space-y-2">
                <button
                  onClick={() => setShowSessionConfirm(true)}
                  disabled={actionLoading !== null}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3.5 bg-purple-600 hover:bg-purple-700 active:bg-purple-800 disabled:opacity-60 text-white rounded-xl font-bold text-base transition-colors"
                >
                  {actionLoading === 'pin_init' ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <span>🚀</span>
                  )}
                  <span>بدأت الجلسة — أرسل رمز التحقق للعميل</span>
                </button>
                {pinError && (
                  <p className="text-xs text-red-600 font-medium flex items-center gap-1 px-1">
                    <span>⚠️</span> {pinError}
                  </p>
                )}
              </div>
            )}

            {/* ── نافذة تأكيد بدء الجلسة ── */}
            {showSessionConfirm && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4" dir="rtl">
                {/* Backdrop */}
                <div
                  className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                  onClick={() => setShowSessionConfirm(false)}
                />
                {/* Card */}
                <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 flex flex-col items-center gap-4 animate-fade-in">
                  {/* Icon */}
                  <div className="w-16 h-16 rounded-2xl bg-purple-100 flex items-center justify-center text-3xl select-none">
                    🎬
                  </div>
                  <div className="text-center space-y-1.5">
                    <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
                      هل بدأت الجلسة فعلاً؟
                    </h3>
                    <p className="text-sm text-slate-500 leading-relaxed" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
                      سيُرسَل رمز التحقق للعميل فور تأكيدك.
                      <br />
                      تأكد أنك مع العميل قبل المتابعة.
                    </p>
                  </div>
                  <div className="w-full flex flex-col gap-2 pt-1">
                    <button
                      onClick={async () => {
                        setShowSessionConfirm(false);
                        setActionLoading('pin_init');
                        setPinError('');
                        try {
                          const { bookingService: bs } = await import('../../services/bookingService');
                          const customerId = booking.customerId || booking.customer_id;
                          const { pin } = await bs.initiateSession(booking.id, customerId);
                          setGeneratedPin(pin);
                          setPinPhase('generated');
                        } catch (err) {
                          setPinError(err.message || 'حدث خطأ، حاول مرة أخرى');
                        } finally {
                          setActionLoading(null);
                        }
                      }}
                      className="w-full py-3 bg-purple-600 hover:bg-purple-700 active:bg-purple-800 text-white rounded-xl font-bold text-sm transition-colors"
                      style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}
                    >
                      نعم، بدأت الجلسة — أرسل الرمز
                    </button>
                    <button
                      onClick={() => setShowSessionConfirm(false)}
                      className="w-full py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold text-sm transition-colors"
                      style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}
                    >
                      لا، رجوع
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* مرحلة: الرمز أُرسل — أدخل ما أعطاك العميل */}
            {pinPhase === 'generated' && (
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-purple-600 text-lg">🔐</span>
                  <p className="text-sm font-semibold text-purple-800">تم إرسال رمز التحقق للعميل</p>
                </div>
                <p className="text-xs text-purple-600">اطلب من العميل الرمز الذي وصله، ثم أدخله هنا:</p>
                <input
                  type="tel"
                  maxLength={4}
                  value={enteredPin}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                    setEnteredPin(val);
                    setPinError('');
                  }}
                  placeholder="_ _ _ _"
                  className="w-full border-2 border-purple-300 focus:border-purple-500 rounded-xl px-4 py-3 text-center text-3xl font-black tracking-[0.6em] text-purple-900 outline-none bg-white"
                />
                {pinError && (
                  <p className="text-xs text-red-600 font-medium flex items-center gap-1">
                    <span>⚠️</span> {pinError}
                  </p>
                )}
                <button
                  onClick={async () => {
                    if (enteredPin.length !== 4) { setPinError('الرمز يجب أن يكون 4 أرقام'); return; }
                    setActionLoading('pin_verify');
                    setPinError('');
                    try {
                      const { bookingService: bs } = await import('../../services/bookingService');
                      const customerId = booking.customerId || booking.customer_id;
                      await bs.verifySessionPin(booking.id, enteredPin, customerId, generatedPin);
                      setPinPhase('verified');
                      setTimeout(() => loadBookings?.(), 800);
                    } catch (err) {
                      setPinError(err.message || 'الرمز غير صحيح');
                    } finally {
                      setActionLoading(null);
                    }
                  }}
                  disabled={actionLoading !== null || enteredPin.length < 4}
                  className="w-full py-3 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-xl font-bold text-sm transition-colors flex items-center justify-center gap-2"
                >
                  {actionLoading === 'pin_verify' ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <span>✓</span>
                      <span>تأكيد الرمز وبدء الجلسة</span>
                    </>
                  )}
                </button>
              </div>
            )}

            {/* تم توليد PIN وتحقق — انتظار إعادة تحميل البيانات */}
            {pinPhase === 'verified' && (
              <div className="bg-purple-100 border border-purple-300 rounded-xl p-3 flex items-center gap-2">
                <span className="text-green-600 text-xl">✅</span>
                <p className="text-sm font-semibold text-purple-800">تم التحقق! الجلسة بدأت رسمياً</p>
              </div>
            )}

            {/* الجلسة مضت دون توثيق بعد نافذة PIN — رسالة إرشادية فقط */}
            {pinWindowEnded && pinPhase === null && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
                <span className="text-amber-500 text-base mt-0.5">⚠️</span>
                <p className="text-xs text-amber-700 leading-relaxed" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
                  لم يتم توثيق هذه الجلسة في وقتها. تواصل مع الدعم لإتمام الحجز.
                </p>
              </div>
            )}
          </div>
        )}

        {/* الجلسة جارية الآن */}
        {booking.status === 'session_started' && (
          <div className="space-y-3">
            <div className="bg-purple-50 border border-purple-300 rounded-xl p-3 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="text-purple-600 text-xl animate-pulse">🎬</span>
                <p className="text-sm font-semibold text-purple-800">الجلسة جارية الآن</p>
              </div>
              <span className="text-xs font-bold text-purple-700 bg-purple-100 px-2 py-1 rounded-lg">موثّقة ✓</span>
            </div>

            {/* زر إنهاء الجلسة الميدانية */}
            <button
              onClick={async () => {
                setActionLoading('complete');
                try {
                  const { data: { session } } = await supabase.auth.getSession();
                  const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
                  const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
                  const res = await fetch(`${SUPABASE_URL}/functions/v1/complete-session`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: `Bearer ${session?.access_token || ANON_KEY}`,
                      apikey: ANON_KEY,
                    },
                    body: JSON.stringify({ bookingId: booking.id }),
                  });
                  const result = await res.json().catch(() => ({}));
                  if (!res.ok) throw new Error(result.error || 'فشل إنهاء الجلسة');
                  setToast({ icon: 'CheckCircle', iconColor: '#22c55e', title: 'تم', message: 'انتقل الحجز لمرحلة تجهيز الصور ✓' });
                  setTimeout(() => loadBookings?.(), 600);
                } catch (err) {
                  setToast({ icon: 'AlertCircle', iconColor: '#ef4444', title: 'خطأ', message: err.message || 'فشل إنهاء الجلسة' });
                } finally {
                  setActionLoading(null);
                }
              }}
              disabled={actionLoading !== null}
              className="w-full flex items-center justify-center gap-2 px-4 py-3.5 bg-green-600 hover:bg-green-700 active:bg-green-800 disabled:opacity-60 text-white rounded-xl font-bold text-base transition-colors"
            >
              {actionLoading === 'complete' ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <AppIcon name="CheckCircle" size={20} color="white" />
              )}
              <span>أنهيت الجلسة الميدانية</span>
            </button>
          </div>
        )}

        {/* في انتظار رفع الملفات — الخطوة التالية بعد إنهاء الجلسة */}
        {booking.status === 'awaiting_delivery' && (
          <div className="space-y-3">
            <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="text-amber-600 text-xl">📂</span>
                <div>
                  <p className="text-sm font-semibold text-amber-800">الجلسة اكتملت — جهّز الصور وارفعها</p>
                  <p className="text-xs text-amber-600">أبلغنا العميل أن صوره في الطريق</p>
                </div>
              </div>
            </div>
            <button
              onClick={() => navigateToDelivery?.()}
              className="w-full flex items-center justify-center gap-2 px-4 py-3.5 bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-white rounded-xl font-bold text-base transition-colors"
            >
              <AppIcon name="Upload" size={20} color="white" />
              <span>ارفع الصور وسلّمها للعميل</span>
            </button>
          </div>
        )}

        {/* مكتمل — تم التسليم */}
        {booking.status === 'completed' && (
          <div className="bg-emerald-50 border border-emerald-300 rounded-xl p-3 flex items-center gap-2">
            <AppIcon name="CheckCircle2" size={20} className="text-emerald-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-emerald-800">تم التسليم بنجاح ✓</p>
              <p className="text-xs text-emerald-600">الصور وصلت للعميل</p>
            </div>
          </div>
        )}

        {/* ── توسيع: تفاصيل العميل والمعدات والتسعير ── */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors w-full justify-center py-1"
        >
          <span>{isExpanded ? 'إخفاء التفاصيل' : 'عرض التفاصيل (العميل، المعدات، الملاحظات…)'}</span>
          <AppIcon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={15} />
        </button>

        {isExpanded && (
          <div className="pt-3 border-t border-slate-100 space-y-3">
            {/* معلومات العميل + أزرار التواصل */}
            <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 bg-primary/10 flex items-center justify-center">
                  {customerAvatar && customerAvatar !== 'https://via.placeholder.com/150' ? (
                    <Image src={customerAvatar} alt={customerName} className="w-full h-full object-cover" />
                  ) : (
                    <AppIcon name="User" size={22} className="text-primary" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-slate-900 mb-2">{customerName}</p>
                  {/* أزرار التواصل بنفس تصميم البطاقة الرئيسية */}
                  {showContactInfo && customerPhone && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <a href={`tel:${customerPhone}`} className="flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-slate-100 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 transition-colors">
                        <AppIcon name="Phone" size={14} className="text-primary" />
                        <span>{customerPhone}</span>
                      </a>
                      <a href={`https://wa.me/${customerPhone?.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg text-sm font-medium text-green-700 transition-colors">
                        <AppIcon name="MessageCircle" size={14} className="text-green-600" />
                        <span>واتساب</span>
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* متطلبات الجلسة: الصور، الفيديوهات، وسائل التصوير */}
            {(booking.deliveredPhotosBucket ||
              booking.deliveredPhotos ||
              booking.shootingMethod ||
              resolvedWorkloadBucket ||
              resolvedProductPhysicalSize) && (
              <div className="bg-white border border-slate-200 rounded-xl p-3">
                <p className="text-xs font-semibold text-slate-700 mb-2">متطلبات الجلسة</p>
                <div className="grid grid-cols-2 gap-2">
                  {(booking.deliveredPhotosBucket || booking.deliveredPhotos) && (
                    <div className="bg-blue-50 rounded-lg px-3 py-2">
                      <p className="text-[10px] text-blue-500 mb-0.5">عدد الصور</p>
                      <p className="text-xs font-bold text-blue-800">
                        {getDeliveredPhotosLabelAr(booking.deliveredPhotosBucket || booking.deliveredPhotos)}
                      </p>
                    </div>
                  )}
                  {booking.videosIncluded && (
                    <div className="bg-purple-50 rounded-lg px-3 py-2">
                      <p className="text-[10px] text-purple-500 mb-0.5">الفيديو</p>
                      <p className="text-xs font-bold text-purple-800">
                        {typeof booking.videosIncluded === 'number'
                          ? `${booking.videosIncluded} فيديو`
                          : booking.videosIncluded === true ? 'مطلوب' : 'غير مطلوب'}
                      </p>
                    </div>
                  )}
                  {(() => {
                    const snap = pricingSnapshot || {};
                    const methods = snap.shootingMethods || (booking.shootingMethod ? [booking.shootingMethod] : []);
                    if (!methods.length) return null;
                    return (
                      <div className="bg-amber-50 rounded-lg px-3 py-2">
                        <p className="text-[10px] text-amber-500 mb-0.5">وسيلة التصوير</p>
                        <p className="text-xs font-bold text-amber-800">
                          {methods.map(m => getShootingMethodLabelAr(m)).filter(Boolean).join('، ')}
                        </p>
                      </div>
                    );
                  })()}
                  {resolvedWorkloadBucket && (
                    <div className="bg-green-50 rounded-lg px-3 py-2">
                      <p className="text-[10px] text-green-500 mb-0.5">حجم العمل</p>
                      <p className="text-xs font-bold text-green-800">
                        {getWorkloadLabelAr(photographyTypeForCard, resolvedWorkloadBucket)}
                      </p>
                    </div>
                  )}
                  {resolvedShootingPlaceType && (
                    <div className="bg-sky-50 rounded-lg px-3 py-2">
                      <p className="text-[10px] text-sky-500 mb-0.5">مكان التصوير</p>
                      <p className="text-xs font-bold text-sky-800">
                        {getShootingPlaceTypeLabelAr(resolvedShootingPlaceType)}
                      </p>
                    </div>
                  )}
                  {photographyTypeForCard === 'product' && resolvedProductPhysicalSize && (
                    <div className="bg-teal-50 rounded-lg px-3 py-2 col-span-2">
                      <p className="text-[10px] text-teal-600 mb-0.5">حجم المنتجات</p>
                      <p className="text-xs font-bold text-teal-900 leading-snug">
                        {getProductPhysicalSizeLabelAr(resolvedProductPhysicalSize)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* مستوى التعديل */}
            {equipment.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl p-3">
                <p className="text-xs font-semibold text-slate-700 mb-2">مستوى التعديل</p>
                <div className="flex flex-wrap gap-1.5">
                  {equipment.map((item, index) => (
                    <span key={index} className="px-2 py-1 bg-primary/10 text-primary rounded-lg text-xs font-medium">
                      {translateEditingItem(item)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* ملاحظات العميل */}
            {booking.notes && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                <p className="text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1">
                  <AppIcon name="MessageSquare" size={12} className="text-amber-500" />
                  ملاحظات العميل
                </p>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{booking.notes}</p>
              </div>
            )}

            {/* عدد المخرجات */}
            {(() => {
              const photosCount = pricingSnapshot?.photosCount ?? pricingSnapshot?.photos_count;
              const videosCount = pricingSnapshot?.videosCount ?? pricingSnapshot?.videos_count;
              const shortVideosCount = pricingSnapshot?.shortVideosCount ?? pricingSnapshot?.short_videos_count;
              const deliverableLabel = pricingSnapshot?.deliverableLabel ?? pricingSnapshot?.deliverable_label;
              const hasDeliverables = pricingSnapshot?.deliverableMode !== 'unspecified' && (photosCount > 0 || videosCount > 0 || shortVideosCount > 0 || deliverableLabel);
              if (!hasDeliverables) return null;
              const label = deliverableLabel || [
                photosCount > 0 ? `${photosCount} صورة` : null,
                videosCount > 0 ? `${videosCount} فيديو طويل` : null,
                shortVideosCount > 0 ? `${shortVideosCount} فيديو قصير` : null,
              ].filter(Boolean).join(' + ');
              return (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                  <p className="text-xs font-semibold text-slate-700 mb-1">عدد المخرجات المطلوبة</p>
                  <p className="text-sm font-bold text-blue-800">{label}</p>
                </div>
              );
            })()}

          </div>
        )}
      </div>
    </div>
  );
};

export default Bookings;
